import { ValidateDealSelectionDocument } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { isAbortError } from '@/lib/utils/is-abort-error';
import { dealRefetchAtom } from '@/states/deal.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';

export const useValidateDealSelection = (dealId: string) => {
  const dealRefetch = useAtomValue(dealRefetchAtom);
  const [validateDealSelectionMutation, { loading, error }] = useMutation(
    ValidateDealSelectionDocument
  );

  const validateDealSelection = async () => {
    trackEvent('DEAL_VALIDATION_STARTED', { dealId });
    try {
      const result = await validateDealSelectionMutation({
        variables: { dealId },
      });
      const response = result.data?.validateDealSelection;
      if (response?.__typename === 'DomainException') {
        trackEvent('DEAL_VALIDATION_FAILED', {
          dealId,
          errorCode: response.code,
        });
      } else {
        trackEvent('DEAL_VALIDATION_SUCCEEDED', { dealId });
      }
      // Best-effort UI refresh: the mutation already succeeded, so a refetch
      // that gets aborted/superseded (e.g. a concurrent deal refresh triggered
      // by this very success) must not turn a successful validation into a
      // thrown error — otherwise the caller's next step (closing the
      // negotiation) is skipped and a false DEAL_VALIDATION_FAILED is tracked.
      // A genuine refetch failure (network/query error) is still logged so it
      // is not silently hidden.
      await dealRefetch.fn().catch((refetchError) => {
        if (!isAbortError(refetchError)) {
          console.error(
            'Failed to refetch deal after validating selection:',
            refetchError
          );
        }
      });
      return result;
    } catch (err) {
      trackEvent('DEAL_VALIDATION_FAILED', {
        dealId,
        errorCode: err instanceof Error ? err.message : 'unknown_error',
      });
      throw err;
    }
  };

  return {
    validateDealSelection,
    loading,
    error,
  };
};
